import type { IndianPokerViewerState } from "@cloudnest/indian-poker-engine";
import {
  type IndianPokerReplayEvent,
  toIndianPokerReplayEvent,
} from "./event-adapter";
import type { IndianPokerReplayFixtureEvent } from "./replay-fixtures.generated";

export interface IndianPokerReplaySnapshot {
  readonly index: number;
  readonly event: IndianPokerReplayFixtureEvent;
  readonly replayEvent: IndianPokerReplayEvent;
  readonly viewerState: IndianPokerViewerState;
}

function assertNoBettingCardLeak(
  viewerState: IndianPokerViewerState,
  sequenceNumber: number,
): void {
  if (viewerState.round.phase !== "BETTING") return;

  const leakedCard = viewerState.round.cards.find(
    (card) => !card.hidden || card.card !== null,
  );
  if (leakedCard) {
    throw new Error(
      `Indian Poker replay event #${sequenceNumber} leaks a betting card for ${leakedCard.playerId}`,
    );
  }
}

function assertNoEventPayloadLeak(event: IndianPokerReplayFixtureEvent): void {
  const serializedPayload = JSON.stringify(event.payload ?? {});
  for (const forbidden of ["state", "deck", "rng", "seed", "cardsByPlayerId"]) {
    if (serializedPayload.includes(forbidden)) {
      throw new Error(
        `Indian Poker replay event #${event.sequenceNumber} leaks ${forbidden}`,
      );
    }
  }
}

export function buildIndianPokerReplaySnapshots(
  events: readonly IndianPokerReplayFixtureEvent[],
): readonly IndianPokerReplaySnapshot[] {
  if (events.length === 0) {
    throw new Error("Cannot replay an empty Indian Poker event log");
  }

  return events.map((event, index) => {
    const replayEvent = toIndianPokerReplayEvent(event);
    assertNoEventPayloadLeak(event);
    assertNoBettingCardLeak(event.viewerState, event.sequenceNumber);
    return {
      index,
      event,
      replayEvent,
      viewerState: event.viewerState,
    };
  });
}
